import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { LucideIcon, Info, BarChart3, Star, Maximize2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { isPremiumActive, PREMIUM_ANALYTICS_HISTORY_DAYS } from "@/lib/premiumFeatures";
import { usePremiumGate } from "@/contexts/PremiumGateContext";
import { Analytics } from "@/lib/analytics";
import {
  formatHealthNumberSmart,
  formatHealthPercentScore,
  formatSleepDuration,
} from "@/lib/formatHealthValue";
import { getLocalDayKey, getLocalDateDaysAgo } from "@/lib/localDateRange";

type MetricKey =
  | "steps"
  | "distance_km"
  | "active_energy_kcal"
  | "resting_heart_rate_bpm"
  | "vo2_max_ml_per_kg_min"
  | "sleep_hours"
  | "flights_climbed"
  | "basal_energy_kcal"
  | "exercise_minutes"
  | "stand_hours"
  | "heart_rate_bpm"
  | "heart_rate_min_bpm"
  | "heart_rate_max_bpm"
  | "heart_rate_avg_bpm"
  | "weight_kg"
  | "body_fat_pct"
  | "height_cm"
  | "bmi"
  | "stress_score"
  | "strain"
  | "hrv_sdnn_ms"
  | "respiratory_rate_per_min";

interface HealthMetricChartProps {
  metric: MetricKey;
  label: string;
  icon: LucideIcon;
  unit: string;
  dbColumn?: string;
  formatter?: (v: number) => number;
  /** When true, show a note instead of nothing when no data */
  showWhenEmpty?: boolean;
  /** Info popup content - shows (i) button when provided */
  infoContent?: string;
  /** Metric key for Analyze - when provided, shows Analyze button that navigates to chat */
  analyzeMetricKey?: MetricKey;
  /** Whether this metric is favorited (for Favorites section) */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

const DB_COLUMN_MAP: Record<MetricKey, string> = {
  steps: "steps",
  distance_km: "distance_m",
  active_energy_kcal: "active_energy_kcal",
  resting_heart_rate_bpm: "resting_heart_rate_bpm",
  vo2_max_ml_per_kg_min: "vo2_max_ml_per_kg_min",
  sleep_hours: "sleep_hours",
  flights_climbed: "flights_climbed",
  basal_energy_kcal: "basal_energy_kcal",
  exercise_minutes: "exercise_minutes",
  stand_hours: "stand_hours",
  heart_rate_bpm: "heart_rate_bpm",
  heart_rate_min_bpm: "heart_rate_min_bpm",
  heart_rate_max_bpm: "heart_rate_max_bpm",
  heart_rate_avg_bpm: "heart_rate_avg_bpm",
  weight_kg: "weight_kg",
  body_fat_pct: "body_fat_pct",
  height_cm: "height_cm",
  bmi: "bmi",
  stress_score: "stress_score",
  strain: "strain",
  hrv_sdnn_ms: "hrv_sdnn_ms",
  respiratory_rate_per_min: "respiratory_rate_per_min",
};

type ChartPoint = { date: string; value: number; formattedDate: string };

/** Metrics where daily SUM across a week/month is meaningful (vs average). */
const SUM_METRICS = new Set<MetricKey>([
  "steps",
  "distance_km",
  "active_energy_kcal",
  "basal_energy_kcal",
  "exercise_minutes",
  "flights_climbed",
  "stand_hours",
]);

function buildAnalyzeMessage(
  label: string,
  analyzeMetricKey: MetricKey,
  fullHistory: boolean,
  series: ChartPoint[],
  unit: string,
): string {
  const metricLogic =
    analyzeMetricKey === "stress_score"
      ? `\n\n=== STRAIV STRESS SCORE (READ CAREFULLY) ===\n- This is a numeric score from 0–100. It is NOT a percentage (do not say "percent" or add a % sign).\n- HIGHER score = BETTER for the user (calmer recovery signal, lower physiological stress).\n- LOWER score = a harder day physiologically (poorer recovery signal in the model).\n- When you interpret trends, "improving" for the user means the line going UP over time (if that matches their goals).\n`
      : analyzeMetricKey === "strain"
        ? `\n\n=== STRAIV STRAIN (READ CAREFULLY) ===\n- Strain is a 0–100 daily LOAD metric (often displayed with a % in the UI). HIGHER strain = more demanding day (activity, cardio load, etc.).\n- Do NOT confuse with Stress Score: Stress Score higher = better recovery; Strain higher = more load. They are different.\n`
        : "";
  const window = fullHistory
    ? "my full Apple Health history for this metric (all available days synced in Straiv, not just the last 30)"
    : "my Apple Health data for this metric over the last 30 days";

  // Cap rows so the chat payload stays manageable; keep earliest + latest coverage.
  const MAX_ROWS = 400;
  let rows = series;
  if (rows.length > MAX_ROWS) {
    const step = Math.ceil(rows.length / MAX_ROWS);
    rows = rows.filter((_, i) => i % step === 0 || i === series.length - 1);
  }
  const table =
    rows.length === 0
      ? "(no data points available in Straiv for this metric yet)"
      : rows.map((p) => `${p.date}: ${p.value}${unit ? ` ${unit}` : ""}`).join("\n");

  return `You are my health coach. Analyze ${window} for "${label}".${metricLogic}

=== METRIC DATA (authoritative — use this table now; do NOT say you are waiting for data) ===
Points: ${series.length} day(s)${rows.length !== series.length ? ` (showing ${rows.length} evenly spaced samples)` : ""}
Date: value
${table}
=== END DATA ===

1) Explain what this metric measures and why it matters.
2) Describe the trend (direction, variability) using the data above — use the correct "higher/lower is better" rules.
3) Give a personalized analysis and concrete, actionable recommendations.

If the table is empty, say so clearly and tell me how to start logging or connect Apple Health on iPhone. Be specific and encouraging. Do not ask me to wait while you fetch data — the numbers are already above.`;
}

function mapRowsToChartData(
  rows: Record<string, unknown>[] | null | undefined,
  col: string,
  metric: MetricKey,
  formatter?: (v: number) => number,
): ChartPoint[] {
  const formatValue = formatter ?? ((v: number) => v);
  const isScore0to100 = metric === "stress_score" || metric === "strain";
  return ((rows ?? []) as Record<string, unknown>[])
    .filter((r) => {
      const val = r[col];
      const num = Number(val);
      if (val == null || Number.isNaN(num)) return false;
      if (isScore0to100) return num >= 0 && num <= 100;
      return num >= 0;
    })
    .map((r) => {
      let val = Number(r[col]);
      if (metric === "distance_km" && col === "distance_m") {
        val = val / 1000;
      }
      if (isScore0to100) {
        val = Math.max(0, Math.min(100, val));
      }
      return {
        date: r.metric_date as string,
        value: formatValue(val),
        formattedDate: new Date(`${r.metric_date as string}T00:00:00`).toLocaleDateString(
          "en-US",
          { month: "short", day: "numeric" },
        ),
      };
    });
}

/** Bucket long histories so 365-day charts stay readable. */
function aggregateForDisplay(
  points: ChartPoint[],
  metric: MetricKey,
): { points: ChartPoint[]; grain: "day" | "week" | "month" } {
  if (points.length <= 45) return { points, grain: "day" };

  const useMonth = points.length > 180;
  const grain: "week" | "month" = useMonth ? "month" : "week";
  const buckets = new Map<string, { sum: number; count: number; date: string }>();

  for (const p of points) {
    const d = new Date(`${p.date}T00:00:00`);
    let key: string;
    let labelDate: string;
    if (grain === "month") {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      labelDate = `${key}-01`;
    } else {
      // ISO week starting Monday
      const day = (d.getDay() + 6) % 7;
      const monday = new Date(d);
      monday.setDate(d.getDate() - day);
      key = getLocalDayKey(monday);
      labelDate = key;
    }
    const cur = buckets.get(key) ?? { sum: 0, count: 0, date: labelDate };
    cur.sum += p.value;
    cur.count += 1;
    buckets.set(key, cur);
  }

  const useSum = SUM_METRICS.has(metric);
  const aggregated = [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, b]) => {
      const value = useSum ? b.sum : b.sum / b.count;
      const formattedDate = new Date(`${b.date}T00:00:00`).toLocaleDateString("en-US", {
        month: "short",
        ...(grain === "month" ? { year: "2-digit" as const } : { day: "numeric" as const }),
      });
      return { date: b.date, value, formattedDate };
    });

  return { points: aggregated, grain };
}

export const HealthMetricChart = ({
  metric,
  label,
  icon: Icon,
  unit,
  dbColumn,
  formatter,
  showWhenEmpty = false,
  infoContent,
  analyzeMetricKey,
  isFavorite,
  onToggleFavorite,
}: HealthMetricChartProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { openPaywall } = usePremiumGate();
  const navigate = useNavigate();
  const col = dbColumn ?? DB_COLUMN_MAP[metric];
  const [data, setData] = useState<ChartPoint[]>([]);
  const [fullData, setFullData] = useState<ChartPoint[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);
  const isPremium = isPremiumActive(profile);

  const fetchData = useCallback(async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const startDate = getLocalDayKey(getLocalDateDaysAgo(30));

        const { data: rows, error } = await supabase
          .from("health_metrics")
          .select(`metric_date, ${col}`)
          .eq("user_id", user.id)
          .gte("metric_date", startDate)
          .order("metric_date", { ascending: true });

        if (error) {
          console.error(`[HealthMetricChart] Fetch error for ${metric}:`, error);
          setLoading(false);
          return;
        }

        setData(mapRowsToChartData(rows as Record<string, unknown>[], col, metric, formatter));
      } catch (e) {
        console.error(`[HealthMetricChart] Error for ${metric}:`, e);
      } finally {
        setLoading(false);
      }
  }, [user?.id, metric, col, formatter]);

  const fetchFullHistory = useCallback(async () => {
    if (!user?.id) return;
    setLoadingFull(true);
    try {
      const startDate = getLocalDayKey(getLocalDateDaysAgo(PREMIUM_ANALYTICS_HISTORY_DAYS));
      const { data: rows, error } = await supabase
        .from("health_metrics")
        .select(`metric_date, ${col}`)
        .eq("user_id", user.id)
        .gte("metric_date", startDate)
        .order("metric_date", { ascending: true });

      if (error) {
        console.error(`[HealthMetricChart] Full history fetch error for ${metric}:`, error);
        return;
      }
      setFullData(mapRowsToChartData(rows as Record<string, unknown>[], col, metric, formatter));
    } catch (e) {
      console.error(`[HealthMetricChart] Full history error for ${metric}:`, e);
    } finally {
      setLoadingFull(false);
    }
  }, [user?.id, metric, col, formatter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch when HealthKit sync completes (app foreground / Health tab)
  useEffect(() => {
    const onSyncComplete = () => {
      fetchData();
      if (expanded) void fetchFullHistory();
    };
    window.addEventListener("health-sync-complete", onSyncComplete);
    return () => window.removeEventListener("health-sync-complete", onSyncComplete);
  }, [fetchData, fetchFullHistory, expanded]);

  const openExpand = () => {
    if (!isPremium) {
      openPaywall({
        title: t('premium.paywallTitle'),
        description: t('premium.healthHistoryLocked'),
        source: 'health_history',
      });
      Analytics.healthHistoryPaywall(metric);
      return;
    }
    setExpanded(true);
    if (!fullData) void fetchFullHistory();
  };

  const runAnalyze = async (fullHistory: boolean) => {
    if (!analyzeMetricKey || !user?.id) return;
    let series = fullHistory ? fullData : data;
    if (fullHistory && (!series || series.length === 0)) {
      setLoadingFull(true);
      try {
        const startDate = getLocalDayKey(getLocalDateDaysAgo(PREMIUM_ANALYTICS_HISTORY_DAYS));
        const { data: rows } = await supabase
          .from("health_metrics")
          .select(`metric_date, ${col}`)
          .eq("user_id", user.id)
          .gte("metric_date", startDate)
          .order("metric_date", { ascending: true });
        series = mapRowsToChartData(rows as Record<string, unknown>[], col, metric, formatter);
        setFullData(series);
      } finally {
        setLoadingFull(false);
      }
    }
    const msg = buildAnalyzeMessage(label, analyzeMetricKey, fullHistory, series ?? [], unit);
    localStorage.setItem('pendingHealthAnalysis', JSON.stringify({
      message: msg,
      _ts: Date.now(),
    }));
    navigate("/chat", {
      state: { prefilledMessage: msg, autoSendHealthAnalysis: true },
    });
  };

  const CustomTooltip = ({
    active,
    payload,
    label: tooltipLabel,
  }: {
    active?: boolean;
    payload?: { value: number }[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    const v = payload[0]?.value ?? 0;
    const displayVal =
      metric === "strain"
        ? formatHealthPercentScore(v)
        : metric === "stress_score"
          ? formatHealthNumberSmart(v)
          : metric === "sleep_hours"
            ? formatSleepDuration(v)
          : metric === "distance_km" || metric === "bmi"
            ? v.toFixed(2)
            : ["vo2_max_ml_per_kg_min", "weight_kg", "body_fat_pct", "hrv_sdnn_ms", "respiratory_rate_per_min"].includes(metric)
              ? formatHealthNumberSmart(v)
              : String(Math.round(v));
    return (
      <div
        className="rounded-lg border-2 border-border p-3 text-sm shadow-lg"
        style={{
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          opacity: 1,
        }}
      >
        <p className="font-semibold mb-1 text-foreground">{tooltipLabel}</p>
        <p className="text-base font-bold text-primary">
          {displayVal}
          {metric !== "strain" && metric !== "stress_score" && metric !== "sleep_hours" ? ` ${unit}` : ""}
        </p>
        {metric === "stress_score" && (
          <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
            {t('components.charts.healthMetric.stressScoreHint')}
          </p>
        )}
      </div>
    );
  };

  const renderChart = (chartData: ChartPoint[], height: number, showGrainHint = false) => {
    const { points: displayPoints, grain } = aggregateForDisplay(chartData, metric);
    const grainHint =
      grain === "week"
        ? t('components.charts.healthMetric.weeklyAverages', { defaultValue: 'Weekly view' })
        : grain === "month"
          ? t('components.charts.healthMetric.monthlyAverages', { defaultValue: 'Monthly view' })
          : null;
    return (
      <div>
        {showGrainHint && grainHint && (
          <p className="text-xs text-muted-foreground mb-2 px-1">{grainHint}</p>
        )}
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={displayPoints} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="formattedDate"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              minTickGap={grain === "day" ? 28 : 16}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              width={metric === "stress_score" || metric === "strain" ? 40 : 36}
              domain={metric === "stress_score" || metric === "strain" ? [0, 100] : ["auto", "auto"]}
              tickFormatter={
                metric === "strain"
                  ? (val: number) => `${Math.round(val)}%`
                  : metric === "stress_score"
                    ? (val: number) => String(Math.round(val))
                    : undefined
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={displayPoints.length <= 40}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="bg-gradient-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-normal text-base">
            <Icon className="h-4 w-4 text-primary" />
            {label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">
            {t('common.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    if (showWhenEmpty) {
      return (
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-normal text-base">
              <Icon className="h-4 w-4 text-primary" />
              {label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t('components.charts.healthMetric.noDataAvailable')}
            </p>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  return (
    <Card className="bg-gradient-card border-0">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 font-normal text-base">
            <Icon className="h-4 w-4 text-primary" />
            {label}
          </CardTitle>
          <div className="flex items-center gap-1">
            {onToggleFavorite && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                aria-label={isFavorite ? t('components.charts.healthMetric.removeFromFavorites') : t('components.charts.healthMetric.addToFavorites')}
              >
                <Star className={`h-4 w-4 ${isFavorite ? "fill-primary text-primary" : ""}`} />
              </Button>
            )}
            {infoContent && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                    <Info className="h-4 w-4" aria-label={t('components.charts.healthMetric.info')} />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="text-base">{label}</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">{infoContent}</p>
                </DialogContent>
              </Dialog>
            )}
            {analyzeMetricKey && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1 text-xs"
                onClick={() => void runAnalyze(false)}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                {t('components.charts.healthMetric.analyze')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                openExpand();
              }}
              aria-label={t('components.charts.healthMetric.expand')}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 md:px-4">
        {renderChart(data, 180)}
      </CardContent>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 pr-6">
              <DialogTitle className="text-base">
                {t('components.charts.healthMetric.expandTitle', { label })}
              </DialogTitle>
              {analyzeMetricKey && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1 text-xs shrink-0"
                  disabled={loadingFull}
                  onClick={() => void runAnalyze(true)}
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  {t('components.charts.healthMetric.analyzeFullHistory')}
                </Button>
              )}
            </div>
          </DialogHeader>
          {loadingFull || !fullData ? (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">
              {t('common.loading')}
            </div>
          ) : fullData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">
              {t('components.charts.healthMetric.noDataAvailable')}
            </p>
          ) : (
            renderChart(fullData, 280, true)
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
